import React, { useState, useEffect, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { Accordion } from "react-bootstrap";
import axios from "axios";
import Swal from "../../messageBox/customSweetAlert";
import { useDispatch } from "react-redux";
import { setLoading } from "../../redux/loaderAction";
import { ADD_TRIP } from "../../constant";
import moment from "moment";

/** Normalize a leg from transData for modal editing and save payload. */
function normalizeTransDataLeg(leg) {
  if (!leg) return leg;
  return {
    ...leg,
    schedule_trans_id: leg.schedule_trans_id ?? leg.scheduleTransId ?? "",
    driver_id:
      leg.driver_id ??
      leg.driver?.user_id ??
      leg.driver?.driver_id ??
      "",
    pick_up_time: leg.pick_up_time ?? leg.pickup_time ?? "",
    drop_off_time: leg.drop_off_time ?? leg.dropoff_time ?? "",
    pickupLocation: leg.pickupLocation ?? leg.pickup_location ?? null,
    dropoffLocation: leg.dropoffLocation ?? leg.dropoff_location ?? null,
    ride_id: leg.ride_id ?? leg.rideId ?? "",
  };
}

const LegDetailsModal = ({
  visible,
  onHide,
  scheduleId,
  legsData,
  scheduleDetails,
  onTripsAdded,
}) => {
  const dispatch = useDispatch();
  const [editedLegs, setEditedLegs] = useState([]);
  const [checkedLegs, setCheckedLegs] = useState([0]); // First leg checked by default
  const [isLoading, setIsLoading] = useState(false);

  const normalizeTimeTo24h = (timeStr) => {
    if (!timeStr) return "";
    const trimmed = String(timeStr).trim();
    const parsed = moment(
      trimmed,
      ["HH:mm", "H:mm", "hh:mm A", "h:mm A", "HH:mm:ss", "hh:mm:ss A"],
      true
    );
    return parsed.isValid() ? parsed.format("HH:mm") : "";
  };

  // Check if any leg has trip_added_via === "1" or 1
  const isDisabled = useMemo(() => {
    if (!legsData || legsData.length === 0) return false;
    return legsData.some((leg) => {
      const tripEntry =
        Array.isArray(leg.trips) && leg.trips.length > 0
          ? leg.trips[0]
          : null;
      return (
        tripEntry &&
        (tripEntry.trip_added_via === "1" || tripEntry.trip_added_via === 1)
      );
    });
  }, [legsData]);

  // Check if current time is before the first leg's pickup time (disable edit until pickup time)
  const isBeforePickupTime = useMemo(() => {
    if (!scheduleDetails?.schedule_date || !editedLegs?.length) return false;
    const firstLeg = editedLegs[0];
    const pickup24 = normalizeTimeTo24h(
      firstLeg.pickup_time || firstLeg.pick_up_time || ""
    );
    if (!pickup24) return false;
    const scheduleDate = moment(scheduleDetails.schedule_date).format("YYYY-MM-DD");
    const pickupMoment = moment(`${scheduleDate} ${pickup24}`, "YYYY-MM-DD HH:mm");
    return moment().isBefore(pickupMoment);
  }, [scheduleDetails?.schedule_date, editedLegs]);

  const editDisabled = isDisabled || isBeforePickupTime;

  useEffect(() => {
    if (legsData && legsData.length > 0) {
      setEditedLegs(
        legsData.map((rawLeg) => {
          const leg = normalizeTransDataLeg(rawLeg);
          // Check if time is valid (not "will call" or empty)
          const isValidTime = (time) => {
            return (
              time && time.toLowerCase() !== "will call" && time.trim() !== ""
            );
          };

          const tripEntry =
            Array.isArray(leg.trips) && leg.trips.length > 0
              ? leg.trips[0]
              : null;
          const derivedMiles = (leg.miles ?? tripEntry?.miles ?? "").toString();

          return {
            ...leg,
            pickup_time: isValidTime(leg.pick_up_time)
              ? normalizeTimeTo24h(leg.pick_up_time)
              : "",
            dropoff_time: isValidTime(leg.drop_off_time)
              ? normalizeTimeTo24h(leg.drop_off_time)
              : "",
            miles: derivedMiles,
            trip_status: Number(leg?.trip_status ? leg?.trip_status : 0),
          };
        })
      );
      // Default-check all legs when data changes
      const allIndices = legsData.map((_, i) => i);
      setCheckedLegs(allIndices);
    }
  }, [legsData, scheduleDetails]);

  const handleInputChange = (index, field, value) => {
    if (editDisabled) return; // Prevent input changes when disabled
    const updatedLegs = [...editedLegs];
    if (field === "pickup_time" || field === "dropoff_time") {
      updatedLegs[index][field] = normalizeTimeTo24h(value);
    } else {
      updatedLegs[index][field] = value;
    }

    setEditedLegs(updatedLegs);
  };

  const handleCheckboxChange = (index) => {
    if (editDisabled) return; // Prevent checkbox changes when disabled
    setCheckedLegs((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleAdd = async () => {
    if (editDisabled) return; // Warning is shown on the modal (alert boxes above)
    // Use checked legs; if none checked, fall back to all legs
    let selectedIndices =
      checkedLegs && checkedLegs.length > 0
        ? checkedLegs
        : editedLegs.map((_, i) => i);
    if (!selectedIndices || selectedIndices.length === 0) {
      Swal.fire({
        title: "Error!",
        text: "No legs to add.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    // Validate that at least one eligible leg is checked
    // if (eligibleChecked.length === 0) {
    //     Swal.fire({
    //         title: 'Error!',
    //         text: 'Please select at least one eligible leg to add.',
    //         icon: 'error',
    //         confirmButtonText: 'OK',
    //     });
    //     return;
    // }

    // Validate that eligible legs have required data
    const hasRequiredData = selectedIndices.some((index) => {
      const leg = editedLegs[index];
      return leg.pickup_time || leg.dropoff_time || leg.miles;
    });

    if (!hasRequiredData) {
      Swal.fire({
        title: "Error!",
        text: "Please enter at least one time or total miles for the selected legs.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    // Additional validations
    // Require pickup and dropoff times for all selected legs
    const missingTimeLegIndex = selectedIndices.findIndex((index) => {
      const leg = editedLegs[index];
      const pickup = normalizeTimeTo24h(leg.pickup_time);
      const dropoff = normalizeTimeTo24h(leg.dropoff_time);
      return !pickup || !dropoff;
    });

    if (missingTimeLegIndex !== -1) {
      Swal.fire({
        title: "Error!",
        text: "Pickup time and Dropoff time are required for the selected legs.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    // 1) Pickup time must be less than dropoff time (when both provided)
    const invalidTimeLegIndex = selectedIndices.findIndex((index) => {
      const leg = editedLegs[index];
      const pickup = normalizeTimeTo24h(leg.pickup_time);
      const dropoff = normalizeTimeTo24h(leg.dropoff_time);
      if (pickup && dropoff) {
        const pickupMoment = moment(pickup, "HH:mm", true);
        const dropoffMoment = moment(dropoff, "HH:mm", true);
        if (pickupMoment.isValid() && dropoffMoment.isValid()) {
          // Enforce: pickup < dropoff
          return pickupMoment.isSameOrAfter(dropoffMoment);
        }
      }
      return false;
    });

    if (invalidTimeLegIndex !== -1) {
      Swal.fire({
        title: "Error!",
        text: "Pickup time must be less than dropoff time for the selected legs.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Build payload for each eligible checked leg
      const payloads = selectedIndices.map((index) => {
        const leg = editedLegs[index];
        //console.log("Add trip leg",leg)
        const scheduleDetail = scheduleDetails || {};

        return {
          schedule_id: scheduleId,
          scheduleTransId: leg.schedule_trans_id || leg.scheduleTransId || "",
          trip_date:
            scheduleDetail.schedule_date || moment().format("YYYY-MM-DD"),
          trip_pick_up_time: leg.pickup_time || "",
          driver_id:
            leg.driver_id ||
            leg.driver?.user_id ||
            leg.driver?.driver_id ||
            "",
          trip_pick_up_location: leg.pickupLocation
            ? `${leg.pickupLocation.pickup_address || ""}, ${leg.pickupLocation.pickup_city || ""
              }, ${leg.pickupLocation.state_master_list?.pickup_state_name || ""
              } ${leg.pickupLocation.pickup_zip || ""}`.trim()
            : leg.pick_up_location || "",
          no_of_leg: scheduleDetail.no_of_leg || "1",
          passenger_id:
            scheduleDetail.passenger?.user_id ||
            scheduleDetail.passenger_id ||
            "",
          odo_meter_pickup: "",
          pickup_latitude: leg?.pickup_latitude || "",
          pickup_longitude: leg?.pickup_longitude || "",
          dropoff_latitude: leg?.dropoff_latitude || "",
          dropoff_longitude: leg?.dropoff_longitude || "",
          vehicle_id: scheduleDetail.vehicle_id || "",
          no_show_flag: leg.no_show_flag || "0",
          trip_drop_off_time: leg.dropoff_time || "",
          trip_drop_off_location: leg.dropoffLocation
            ? `${leg.dropoffLocation.dropoff_address || ""}, ${leg.dropoffLocation.dropoff_city || ""
              }, ${leg.dropoffLocation.state_master_list?.dropOff_state_name || ""
              } ${leg.dropoffLocation.dropoff_zip || ""}`.trim()
            : leg.drop_off_location || "",
          odo_meter_dropoff: "",
          miles: leg.miles || "",
          trip_status: Number(leg.trip_status || 0),
          trip_added_via: 0,
          payer: scheduleDetails?.passenger?.passenger?.payers?.payer_name ?? "",
        };
      });

      // Send all legs in a single API call (payload as array)
      const response = await axios.post(ADD_TRIP, payloads);

      // Check if the request was successful
      const allSuccessful = response.data?.status === "200";

      setIsLoading(false);

      if (allSuccessful) {
        Swal.fire({
          title: "Success!",
          text: `Successfully added ${payloads.length} trip(s)`,
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          onHide();
          if (typeof onTripsAdded === "function") {
            onTripsAdded();
          }
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: "Some trips could not be added. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error adding trips:", error);
      Swal.fire({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Failed to add trips. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={`Leg Details - Schedule #${scheduleId}`}
      style={{ width: "70vw", maxHeight: "80vh" }}
      modal
      footer={
        <div className="d-flex justify-content-center gap-3">
          <button
            className="btn rounded-1"
            onClick={onHide}
            disabled={isLoading}
            style={{
              backgroundColor: "rgb(249, 174, 59)",
              borderColor: "rgb(249, 174, 59)",
              color: "#fff",
              borderRadius: 0,
              width: "160px",
            }}
          >
            Close
          </button>
          <button
            className="btn rounded-1"
            onClick={handleAdd}
            disabled={isLoading || editDisabled}
            style={{
              backgroundColor: editDisabled
                ? "#6c757d"
                : "rgb(49, 135, 111)",
              borderColor: editDisabled ? "#6c757d" : "rgb(49, 135, 111)",
              color: "#fff",
              borderRadius: 0,
              width: "160px",
              cursor: editDisabled ? "not-allowed" : "pointer",
            }}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Saving...
              </>
            ) : (
              "Save Trip"
            )}
          </button>
        </div>
      }
    >
      {isLoading && (
        <div
          className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 1000,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-primary mb-3"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5>Saving trips...</h5>
            <p className="text-muted">
              Please wait while we process your request
            </p>
          </div>
        </div>
      )}
      {isDisabled && (
        <div
          className="alert alert-warning mb-3"
          style={{
            backgroundColor: "#fff3cd",
            borderColor: "#ffc107",
            color: "#856404",
            marginBottom: "1rem",
          }}
        >
          <span>⚠️</span>
          You are not allowed to edit or add trip data from mobile app
        </div>
      )}

      {isBeforePickupTime && (
        <div
          className="alert alert-warning mb-3"
          style={{
            backgroundColor: "#fff3cd",
            borderColor: "#ffc107",
            color: "#856404",
            marginBottom: "1rem",
          }}
        >
          <span>⚠️</span>
          You are not allowed to edit or add trip before the pick up time
        </div>
      )}
      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        {editedLegs && editedLegs.length > 0 ? (
          <Accordion defaultActiveKey="0">
            {editedLegs.map((leg, index) => (
              <Accordion.Item eventKey={index.toString()} key={index}>
                <Accordion.Header
                  style={{
                    backgroundColor: "rgba(205, 228, 218, 0.29)",
                    "--bs-accordion-btn-bg": "rgba(205, 228, 218, 0.29)",
                    "--bs-accordion-active-bg": "rgba(205, 228, 218, 0.29)",
                  }}
                >
                  <div
                    className="d-flex align-items-center w-100"
                    style={{ backgroundColor: "rgba(205, 228, 218, 0.29)" }}
                  >
                    <input
                      type="checkbox"
                      className="me-3"
                      style={{ width: "18px", height: "18px" }}
                      checked={checkedLegs.includes(index)}
                      onChange={() => handleCheckboxChange(index)}
                      disabled={editDisabled}
                    />
                    <span style={{ fontWeight: "bold" }}>
                      Leg {index + 1} ({leg.ride_id || "N/A"})
                    </span>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="pickup-detail mb-4">
                        <div>
                          <label
                            className="form-label me-2 text-nowrap"
                            style={{ fontSize: "14px", fontWeight: "500" }}
                          >
                            Pickup Location:
                          </label>
                        </div>
                        <div style={{ paddingLeft: "5px" }}>
                          <span>
                            {leg.pickupLocation?.pickup_location}
                            {`  ${leg.pickupLocation?.state_master_list?.pickup_state_name}, `}
                            {leg.pickupLocation?.pickup_zip}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="pickup-detail mb-4">
                        <div>
                          <label
                            className="form-label me-2 text-nowrap"
                            style={{ fontSize: "14px", fontWeight: "500" }}
                          >
                            Dropoff Location:
                          </label>
                        </div>
                        <div style={{ paddingLeft: "13px" }}>
                          <span>
                            {leg.dropoffLocation?.dropoff_location}
                            {`  ${leg.dropoffLocation?.state_master_list?.dropOff_state_name}, `}
                            {`  ${leg.dropoffLocation?.dropoff_zip}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="pickup-detail">
                        <div>
                          <label
                            className="form-label me-2"
                            style={{ fontSize: "14px", fontWeight: "500" }}
                          >
                            Pickup Time:
                          </label>
                        </div>
                        <div>
                          <input
                            type="time"
                            className="form-control"
                            value={normalizeTimeTo24h(leg.pickup_time)}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "pickup_time",
                                e.target.value
                              )
                            }
                            placeholder="Select pickup time"
                            style={{
                              maxWidth: "200px",
                              borderColor: !leg.pickup_time
                                ? "#ffc107"
                                : "#ced4da",
                              borderWidth: !leg.pickup_time ? "2px" : "1px",
                            }}
                            disabled={editDisabled}
                          />
                          {!leg.pickup_time && (
                            <small className="text-warning">
                              Time not set - please select a time
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="pickup-detail">
                        <div>
                          <label
                            className="form-label me-2"
                            style={{ fontSize: "14px", fontWeight: "500" }}
                          >
                            Dropoff Time:
                          </label>
                        </div>
                        <div>
                          <input
                            type="time"
                            className="form-control"
                            value={normalizeTimeTo24h(leg.dropoff_time)}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "dropoff_time",
                                e.target.value
                              )
                            }
                            placeholder="Select dropoff time"
                            style={{
                              maxWidth: "200px",
                              borderColor: !leg.dropoff_time
                                ? "#ffc107"
                                : "#ced4da",
                              borderWidth: !leg.dropoff_time ? "2px" : "1px",
                            }}
                            disabled={editDisabled}
                          />
                          {!leg.dropoff_time && (
                            <small className="text-warning">
                              Time not set - please select a time
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label
                            className="form-label"
                            style={{ fontSize: "14px", fontWeight: "500" }}
                          >
                            Total Miles:
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            value={leg.miles || ""}
                            onChange={(e) =>
                              handleInputChange(index, "miles", e.target.value)
                            }
                            style={{
                              fontWeight: "bold",
                            }}
                            step="0.01"
                            min="0"
                            disabled={editDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        ) : (
          <div className="text-center p-4">
            <p>No leg details available</p>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default LegDetailsModal;
